import React, { useEffect, useMemo, useState } from 'react';
import * as R from 'ramda';

// @ts-ignore
import renameIcon from '@atom/components/common/svgIcons/renameIcon.svg';
import { CalendarGap } from '@atom/components/workOrders/workOrdersCalendarNew/weekViewUtils';
import { usePreferences } from '@atom/hooks/usePreferences';
import { useSchemas } from '@atom/hooks/useSchemas';
import {
  DatePicker,
  DateTimePicker,
  Icon,
  List,
  Switch,
  TextField,
} from '@atom/mui';
import { InventoryAssetDetailType } from '@atom/types/inventory';
import { WorkOrderType } from '@atom/types/work';
import {
  SearchWorkTemplate,
  WorkTemplateListItem,
} from '@atom/types/workTemplate';
import {
  convertMinutesToMilliseconds,
  getUpdatedOtherDate,
  roundUpByMilliseconds,
} from '@atom/utilities/timeUtilities';
import { getWorkTypeIconSvg } from '@atom/utilities/workTypeIconUtilities';

import InventoryAssetTile from '../inventoryAssetTree/InventoryAssetTile';

import { getStartAndDueDateInitialValues } from './createWorkUtilities';

import './createWorkModals.css';

const { ListItem, ListItemText } = List;

interface Props {
  name: string;
  onNameChange: (name: string) => void;
  dueDate?: Date;
  onDueDateChange: (date: Date) => void;
  plannedStartDate: Date;
  setPlannedStartDate: (date: Date) => void;
  asset: InventoryAssetDetailType;
  onAssetChange: (asset: InventoryAssetDetailType) => void;
  onAssetViewClick: () => void;
  isLocationBased: boolean;
  onLocationBasedChange: (value: boolean) => void;
  workTemplate?: WorkTemplateListItem | SearchWorkTemplate;
  disableAssetChange?: boolean;
  requireAsset?: boolean;
  fromCalendarDate?: Date;
  fromCalendarGap?: CalendarGap;
}

const styles = {
  icon: {
    marginRight: '2rem',
  },
};

const CreateSingleWork = ({
  name,
  onNameChange,
  dueDate,
  onDueDateChange,
  plannedStartDate,
  setPlannedStartDate,
  asset,
  onAssetChange,
  onAssetViewClick,
  isLocationBased,
  onLocationBasedChange,
  workTemplate,
  disableAssetChange,
  requireAsset,
  fromCalendarDate,
  fromCalendarGap,
}: Props) => {
  const { schemas = [] } = useSchemas();
  const preferences = usePreferences();
  const isWorkNamingDisabled = preferences.workOrders?.workNamingDisabled;

  const FIFTEEN_MINUTES_IN_MILLISECONDS = convertMinutesToMilliseconds(15);

  // These are used to force re-render of Date Picker when input changes
  // via typing or copy paste. This will no longer be needed once MUI is upgraded.
  const [plannedChange, setPlannedChange] = useState<boolean>(false);
  const [dueChange, setDueChange] = useState<boolean>(false);

  const workTemplateSchema = useMemo(() => {
    return schemas.find(({ id }) => id === workTemplate?.schemaId);
  }, [workTemplate]);

  useEffect(() => {
    // Only initialize range if both are set to initial value
    const formContainsDefaultRange =
      dueDate?.valueOf() === plannedStartDate?.valueOf();
    if (formContainsDefaultRange) {
      if (workTemplate?.appointmentsEnabled) {
        const values = getStartAndDueDateInitialValues({
          fromCalendarDate,
          fromCalendarGap,
        });

        setPlannedStartDate(values.plannedStartDate);
        onDueDateChange(values.dueDate);
      } else if (fromCalendarDate) {
        // for non-appointment templates
        // - set the dueDate if created from a calendar day
        onDueDateChange(fromCalendarDate);
      }
    }
  }, [workTemplate]);

  const handleDueDateTimeChange = (date?: Date) => {
    if (!date) {
      // Do not allow user to clear date once set
      onDueDateChange(dueDate);
    } else {
      const roundedDate = roundUpByMilliseconds(
        date,
        FIFTEEN_MINUTES_IN_MILLISECONDS,
      );

      // Ensures date times are on the same day
      const updatedPlannedStartDate = getUpdatedOtherDate(
        roundedDate,
        plannedStartDate,
      );

      onDueDateChange(roundedDate);
      setPlannedStartDate(updatedPlannedStartDate);
    }
  };

  const handlePlannedStartDateTimeChange = (date?: Date) => {
    if (!date) {
      // Do not allow user to clear date once set
      setPlannedStartDate(plannedStartDate);
    } else {
      const roundedDate = roundUpByMilliseconds(
        date,
        FIFTEEN_MINUTES_IN_MILLISECONDS,
      );

      // Ensures date times are on the same day
      const updatedDueDate = getUpdatedOtherDate(roundedDate, dueDate);

      setPlannedStartDate(roundedDate);
      onDueDateChange(updatedDueDate);
    }
  };

  const handleDueDateChange = (date?: Date) => {
    if (!date) {
      onDueDateChange(null);
    } else {
      onDueDateChange(date);
    }
  };

  const workTemplateType = R.pathOr(
    WorkOrderType.DEFAULT,
    ['type'],
    workTemplate,
  );
  const showAssetAndLocation = workTemplateType === WorkOrderType.DEFAULT;
  const schemaSubtext =
    workTemplateType === WorkOrderType.DEFAULT
      ? workTemplateSchema?.name || 'All Asset Types'
      : '';

  const errorDateBoundary = useMemo(() => {
    if (workTemplate?.appointmentsEnabled) {
      return plannedStartDate?.valueOf() > dueDate?.valueOf();
    }

    return false;
  }, [dueDate, plannedStartDate, workTemplate]);

  return (
    <>
      {workTemplate && (
        <div styleName="modal-row work-template">
          <Icon style={styles.icon}>
            <img
              src={getWorkTypeIconSvg(
                workTemplate?.isParent,
                workTemplate?.parentId,
              )}
            />
          </Icon>
          <ListItem disableGutters style={{ margin: 0, padding: 0 }}>
            <ListItemText
              style={{ margin: 0 }}
              primary={workTemplate.name}
              secondary={schemaSubtext}
            />
          </ListItem>
        </div>
      )}
      <div styleName="modal-row name">
        <Icon style={styles.icon}>
          <img src={renameIcon} />
        </Icon>
        <TextField
          required
          id="workOrderName"
          label="Work Name"
          value={name}
          onChange={event => onNameChange(event.target.value)}
          disabled={!!isWorkNamingDisabled}
        />
      </div>
      {workTemplate?.appointmentsEnabled ? (
        <>
          <div styleName="modal-row due-date">
            <Icon style={styles.icon}>event</Icon>
            <DateTimePicker
              key={`planned${plannedChange}`}
              label="Planned Start Date *"
              value={plannedStartDate}
              onChange={handlePlannedStartDateTimeChange}
              TextFieldProps={{
                onBlur: () => setPlannedChange(!plannedChange),
              }}
            />
          </div>
          <div styleName="modal-row due-date">
            <div styleName="icon-spacer" />
            <DateTimePicker
              key={`due${dueChange}`}
              label="Due Date *"
              value={dueDate}
              onChange={handleDueDateTimeChange}
              errorHelperText={
                errorDateBoundary &&
                'Due time must be after planned start time.'
              }
              TextFieldProps={{
                error: errorDateBoundary,
                onBlur: () => setDueChange(!dueChange),
              }}
            />
          </div>
        </>
      ) : (
        <div styleName="modal-row due-date">
          <Icon style={styles.icon}>event</Icon>
          <DatePicker
            label="Due Date"
            value={dueDate}
            onChange={handleDueDateChange}
          />
        </div>
      )}
      {showAssetAndLocation && (
        <>
          <div styleName="modal-row">
            <Icon style={styles.icon}>category</Icon>
            <InventoryAssetTile
              asset={asset}
              onClick={onAssetViewClick}
              onRemove={!disableAssetChange ? () => onAssetChange(null) : null}
              requireAsset={requireAsset}
            />
          </div>
          <div style={{ marginTop: '0.5rem' }}>
            <Switch
              checked={isLocationBased}
              onChange={() => onLocationBasedChange(!isLocationBased)}
              name="isLocationBased"
              label="Location"
              disabled={!!asset}
            />
          </div>
        </>
      )}
    </>
  );
};

export default CreateSingleWork;
